import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Hourglass, Loader2 } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'
import type { Progress } from '../v4/contracts'
import AgentDot from './AgentDot'

/**
 * What the engine is doing now (the old chat's "Planning ▾" indicator, Figma Generating screens):
 * the current phase in orange with its detail, the phases before as a checked list, a spinner on
 * the one in flight. While the run waits on the engineer (approval, an ask) the row reads amber and
 * nothing spins: that time is theirs. Drawn from the engine's progress (server/progress.ts via the
 * hook), so the words are the engine's. Nothing once the run finished: the result card takes over.
 *
 * One clock only: the grey "Waiting for the model · N s" while a call is out. No phase clock in the
 * header, no durations on the done phases, and no "slow to reply" wording (Pegah, 2026-11-01).
 */
export default function RunActivity({ progress, receivedAt }: { progress: Progress; receivedAt: number }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)
  const [, setTick] = useState(0)
  useEffect(() => {
    if (progress.finished) return
    const id = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [progress.finished])

  if (progress.finished || progress.phases.length === 0) return null
  // The server's clock, moved on by the time since the progress came.
  const now = progress.now + (Date.now() - receivedAt)
  const cur = progress.phases[progress.phases.length - 1]
  const before = progress.phases.slice(0, -1)
  const paused = Boolean(cur.yours)
  const waitS = progress.modelSince !== undefined && !paused ? Math.max(0, Math.floor((now - progress.modelSince) / 1000)) : null
  const step = progress.steps?.current
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <div aria-live="polite">
      <button
        type="button"
        aria-expanded={open}
        disabled={before.length === 0}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center gap-2 text-left text-sm font-medium',
          paused ? 'text-amber-600 dark:text-amber-500' : 'text-orange-600 dark:text-orange-500'
        )}
      >
        <AgentDot />
        <span className={cn('min-w-0 flex-1 truncate', !paused && 'animate-pulse')}>{cur.title}</span>
        {before.length > 0 && <Chevron className="size-4 shrink-0 text-muted-foreground" />}
      </button>
      {(cur.detail || step || waitS !== null) && (
        <div className="mt-1 space-y-0.5 pl-6 text-xs text-muted-foreground">
          {cur.detail && <p>{cur.detail}</p>}
          {step && progress.steps && (
            <p>
              {t('workflow:builder.activity_step', {
                defaultValue: 'Step {{n}} of {{total}}: {{title}}',
                n: step.n,
                total: progress.steps.total,
                title: step.title,
              })}
              {step.activity ? ` · ${step.activity}` : ''}
            </p>
          )}
          {waitS !== null && <p>{t('workflow:builder.activity_wait', { defaultValue: 'Waiting for the model · {{s}} s', s: waitS })}</p>}
        </div>
      )}
      {open && before.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border">
          {before.map((p, i) => (
            <div key={i} className="flex items-center gap-2.5 border-b border-border px-3 py-2 text-sm text-foreground">
              <Checkbox checked disabled aria-hidden />
              <span className="min-w-0 flex-1 truncate">
                {p.title}
                {p.detail && <span className="text-muted-foreground"> · {p.detail}</span>}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-2.5 px-3 py-2 text-sm text-foreground">
            {paused ? (
              <Hourglass className="size-4 shrink-0 text-amber-600 dark:text-amber-500" aria-hidden />
            ) : (
              <Loader2 className="size-4 shrink-0 animate-spin text-orange-600 dark:text-orange-500" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate">{cur.title}</span>
          </div>
        </div>
      )}
    </div>
  )
}
